import {
  buildCDNURL,
  getTitleMax,
  handleHtmlTags,
  toISO8601WithOffset,
  trimString,
} from "@platformx/utilities";
import { buildContentUrl, omitEmpty } from "./common";

export type ArticleStructureDataInput = {
  /** Article `CommonFields`, or the flattened content record from a listing action. */
  content?: any;
  /** `ObjectFields.original_image` — needed for a real CDN image URL. */
  originalImage?: any;
  keywords?: any;
  /** Slug (preferred) or page id used in the delivery route. */
  pageUrl?: string;
  /** Brand segment when multi-brand is on, otherwise the language code. */
  selectedBrandRoute?: string;
};

/**
 * `NewsArticle` markup for an Article.
 *
 * Fixes the five mappings the SEO audit raised (KIWI-6282):
 * - `@type` is `NewsArticle`, not `Article` — accepted as a safe global default
 *   for every tenant, news site or not.
 * - lowercase `description` (`Description` is not a schema.org property) sourced
 *   from the author-entered `short_description`, NOT `description`, which holds
 *   the rich-text editor's body HTML.
 * - `image` is a resolved CDN URL, not the raw DSpace bitstream GUID.
 * - dates are ISO-8601 with offset instead of `"-"` / `"MMM d, yyyy | hh:mm a"`.
 * - `author.name` is populated regardless of the `show_author` display toggle.
 */
export const buildArticleStructureData = ({
  content = {},
  originalImage = {},
  keywords = [],
  pageUrl = "",
  selectedBrandRoute = "en",
}: ArticleStructureDataInput = {}) => {
  const { original_image_relative_path: relativePath = "", ext = "" } = originalImage || {};
  const image = relativePath ? buildCDNURL(relativePath, ext) : originalImage?.Thumbnail || "";

  // A summary, not the body: short_description is plain text the author typed
  // and is required on publish; seo_description is the settings-tab equivalent.
  const description = handleHtmlTags(
    content?.short_description || content?.settings?.seo_description || "",
  );

  // page_createdby is only stamped at save time, so an article being edited
  // falls back to the hydrated createdBy — this is why author.name was blank.
  const authorName =
    content?.page_createdby ||
    content?.createdBy ||
    content?.page_lastmodifiedby ||
    content?.lastModifiedBy ||
    "";

  const now = new Date().toISOString();

  // `datePublished` is the article's real publication instant and must never be
  // fabricated: falling back to `now` re-stamped it on EVERY save, so a
  // re-published article claimed it was published today and datePublished came
  // out LATER than dateModified (KIWI-6280 E2E). An unknown date is omitted.
  // lastPublishedDate is the backend's real publication instant and is the only
  // one of these that is stable across saves; creationDate is the last resort.
  const publishedAt =
    content?.published_date ||
    content?.publishedDate ||
    content?.lastPublishedDate ||
    content?.creationDate ||
    "";

  return omitEmpty({
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: trimString(handleHtmlTags(content?.title), getTitleMax("article")),
    description,
    keywords,
    image,
    url: buildContentUrl("article", pageUrl, selectedBrandRoute),
    datePublished: toISO8601WithOffset(publishedAt),
    dateModified: toISO8601WithOffset(content?.modificationDate || now),
    author: authorName ? [{ "@type": "Person", name: authorName }] : [],
  });
};
